import { useId } from 'react';
import { MeshFill, IS_WEBKIT } from './MeshFill';
import { SHIELD_PATH, SWORD_PATH, STAR_V, STAR_H, CRACKS, taperedPolygon } from './PaladinLogo';

type Colors6 = [string, string, string, string, string, string];

const INK = '#020A10';
const BACKDROP = '#020C12'; // the sky behind the crest, for WebKit's painted cut-outs
const SEAM_TRANSFORM = 'translate(32 30) scale(0.91) translate(-32 -30)';
const MIRROR = 'scale(-1,1) translate(-64,0)';

/* Emblem in visual coordinates (star upper-left, hilt upper-right, blade driven
   down-left into the rock). Sword is drawn upright at the origin, then rotated. */
const SWORD_PLACE = 'translate(35.5 31) rotate(30) scale(1.05)';
const SWORD_SCALE = 1.05;
const ROCK_INSET = 'translate(32 30) scale(0.85) translate(-32 -30)';
const SWORD_HOLES = [
  'M0 -16.6 L1.5 -15 L0 -13.4 L-1.5 -15 Z', // pommel
  'M-0.85 -13.6 L0.85 -13.6 L0.85 -9.6 L-0.85 -9.6 Z', // grip
  'M-6 -6.9 L-4.6 -8.4 L-1.5 -8.2 L0 -7.6 L1.5 -8.2 L4.6 -8.4 L6 -6.9 L5.5 -9.8 L1.8 -10.4 L0 -10.8 L-1.8 -10.4 L-5.5 -9.8 Z', // crossguard, tips swept toward the blade
  'M-2.2 -7.1 L2.2 -7.1 L2.2 9.8 L0 14.6 L-2.2 9.8 Z', // blade
];
const SWORD_DETAILS = [
  'M-0.4 -5.6 L0.4 -5.6 L0.4 9.4 L0 11 L-0.4 9.4 Z', // center ridge
];
const GEM = { cx: 0, cy: -9.2, r: 0.85 };

/* Original stone: the third subpath of the source icon (relative start resolved) */
const STONE = 'M347.906 258.53' + SWORD_PATH.slice(SWORD_PATH.indexOf('m169.094 111.53') + 'm169.094 111.53'.length);

/* Cracked state: the whole lower section (below where the rock meets the rim)
   breaks along a jagged line into three chunks that fall away. Visual coords. */
const BREAK = 'L13 43.5 L17 45.2 L20 43.6 L23.5 46 L27 44.2 L30.5 46.8 L33.5 44.5 L37 46.4 L40.5 44 L44 45.8 L47.5 43.4 L51 44.8';
const BROKEN_TIP = `M10 43.5 ${BREAK} L54 44.8 L54 64 L10 64 Z`;
const FRAGMENTS = [
  { d: 'M10 43.5 L13 43.5 L17 45.2 L20 43.6 L23.5 46 L27 44.2 L25.6 48 L27.4 51 L26 64 L10 64 Z', dx: -1, dy: 1.8, deg: -5, ox: 20, oy: 50 },
  { d: 'M27 44.2 L30.5 46.8 L33.5 44.5 L37 46.4 L38.2 50 L36.4 53 L37.5 64 L26 64 L27.4 51 L25.6 48 Z', dx: 0, dy: 2.6, deg: 2, ox: 32, oy: 52 },
  { d: 'M37 46.4 L40.5 44 L44 45.8 L47.5 43.4 L51 44.8 L54 44.8 L54 64 L37.5 64 L36.4 53 L38.2 50 Z', dx: 1.2, dy: 1.6, deg: 6, ox: 44, oy: 48 },
];

/* Upper body splits along the main fracture (visual coords); each half drifts
   away from the center on the hit and coalesces back during the heal */
const FISSURE = 'L22.8 1.5 L23.2 4.5 L25.4 9.5 L24.5 13 L26 18 L27.8 24 L26.6 27.5 L28.9 35 L28.2 39 L30.4 47 L30 51 L31.4 57';
const HALVES = [
  { d: `M-10 -10 L22.8 -10 ${FISSURE} L31.4 74 L-10 74 Z`, dx: -1.1, dy: 0.2, deg: -2, ox: 24, oy: 32 },
  { d: `M22.8 -10 L74 -10 L74 74 L31.4 74 L31.4 57 L30 51 L30.4 47 L28.2 39 L28.9 35 L26.6 27.5 L27.8 24 L26 18 L24.5 13 L25.4 9.5 L23.2 4.5 L22.8 1.5 Z`, dx: 1.1, dy: 0, deg: 2.5, ox: 40, oy: 32 },
];

type Phase = 'rest' | 'damage' | 'hold' | 'unwarp' | 'heal' | 'fade';

/** Lock-style crest: flat mesh fill, one hard ink outline, inset seam, soft pressed
    bevel on every cut. Phase-driven: cracks spread and the lower section breaks
    off on the hit, then the pieces pull back together and the cracks close. */
/* WebKit can't tween the CSS `d` property, so there the cracks are set as attributes (MeshFill
   handles the fill side of WebKit's quirks). */

export function PixelCrest({
  size = 360,
  colors,
  speed = 0.2,
  phase = 'rest',
}: {
  size?: number;
  colors: Colors6;
  speed?: number;
  phase?: Phase;
}) {
  const id = `crest${useId().replace(/:/g, '')}`;
  const broken = phase === 'damage' || phase === 'hold' || phase === 'unwarp';
  const snap = phase === 'damage';
  const settled = phase === 'rest' || phase === 'fade';
  const pieceStyle = (p: { dx: number; dy: number; deg: number; ox: number; oy: number }): React.CSSProperties => ({
    transform: broken ? `translate(${p.dx}px, ${p.dy}px) rotate(${p.deg}deg)` : 'none',
    transformOrigin: `${p.ox}px ${p.oy}px`,
    transformBox: 'view-box',
    transition: snap ? 'transform 0.35s cubic-bezier(0.3, 0, 0.6, 1)' : 'transform 1.8s cubic-bezier(0.2, 0.8, 0.3, 1)',
  });
  const crackTransition = snap ? 'none' : 'd 2.8s ease-in-out';
  // A zero-width crack polygon still strokes as a hairline, so fade the stroked copies out
  const crackStrokeStyle: React.CSSProperties = { opacity: broken ? 1 : 0, transition: snap ? 'none' : 'opacity 2.8s ease-in-out' };

  const crackLines = (props: React.SVGProps<SVGPathElement>, animated: boolean) =>
    CRACKS.map((c, i) => {
      const shape = taperedPolygon(c, animated ? (broken ? 1.25 : 0) : 1.25);
      return IS_WEBKIT
        ? <path key={i} {...props} d={shape} />
        : <path key={i} {...props} style={{ d: `path("${shape}")`, transition: crackTransition } as React.CSSProperties} />;
    });
  const breakEdge = (props: React.SVGProps<SVGPathElement>) => <path d={BROKEN_TIP} transform={MIRROR} {...props} />;

  const stone = (props: React.SVGProps<SVGPathElement>) => (
    <g clipPath={`url(#${id}-rock)`}>
      <g transform="translate(8, 9) scale(0.096)">
        <path d={STONE} {...props} strokeWidth={props.strokeWidth ? Number(props.strokeWidth) / 0.096 : undefined} />
      </g>
    </g>
  );
  const holes = (props: React.SVGProps<SVGPathElement>) => (
    <>
      <path d={STAR_V} {...props} />
      <path d={STAR_H} {...props} />
      <g transform={MIRROR}>
        <g transform={SWORD_PLACE}>
          {SWORD_HOLES.map(d => <path key={d} d={d} {...props} strokeWidth={props.strokeWidth ? Number(props.strokeWidth) / SWORD_SCALE : undefined} />)}
        </g>
      </g>
    </>
  );
  const details = (
    <g transform={MIRROR} fill="white">
      <g transform={SWORD_PLACE}>
        {SWORD_DETAILS.map(d => <path key={d} d={d} />)}
        <circle {...GEM} />
      </g>
    </g>
  );

  /* sfx '' = main body (cracks + lower section removed); '-whole' = intact body used for the fragments */
  const maskDefs = (sfx: string, main: boolean) => {
    const cuts = main ? <>{crackLines({ fill: 'black' }, true)}{breakEdge({ fill: 'black' })}</> : null;
    return (
      <>
        <mask id={`${id}-fill${sfx}`} maskUnits="userSpaceOnUse" x="-10" y="-10" width="84" height="84">
          <path d={SHIELD_PATH} fill="white" />
          {holes({ fill: 'black' })}
          {details}
          {/* Metal gap between blade and rock — the sword "floats" into the stone */}
          {stone({ fill: 'none', stroke: 'white', strokeWidth: 2.4 })}
          {stone({ fill: 'black' })}
          <path d={SHIELD_PATH} transform={SEAM_TRANSFORM} fill="none" stroke="black" strokeWidth={0.9} />
          <path d={SHIELD_PATH} fill="none" stroke="white" strokeWidth={3.4} clipPath={`url(#${id}-inside)`} />
          {cuts}
        </mask>
        <mask id={`${id}-cracks${sfx}`} maskUnits="userSpaceOnUse" x="-10" y="-10" width="84" height="84">
          <rect x="-10" y="-10" width="84" height="84" fill="white" />
          {cuts}
        </mask>
        <mask id={`${id}-edge${sfx}`} maskUnits="userSpaceOnUse" x="-10" y="-10" width="84" height="84">
          <rect x="-10" y="-10" width="84" height="84" fill="white" />
          <path d={SHIELD_PATH} fill="black" />
          {cuts}
        </mask>
      </>
    );
  };

  /* iOS WebKit draws nothing through these masks, so there the crest is painted in layers instead:
     the body in the mesh tones, then every cut-out painted over in the backdrop colour. */
  const paintedLayers = (main: boolean) => (
    <>
      <path d={SHIELD_PATH} transform="translate(0 1.4)" fill={INK} stroke={INK} strokeWidth={2.2} />
      {/* Outline behind the body, so only its outer half shows, as the masked version draws it */}
      <path d={SHIELD_PATH} fill="none" stroke={INK} strokeWidth={2.2} strokeLinejoin="round" />
      <path d={SHIELD_PATH} fill={`url(#${id}-tone)`} />
      {/* Sheen and shade standing in for the mesh's moving highlights */}
      <path d={SHIELD_PATH} fill={`url(#${id}-sheen)`} />
      <path d={SHIELD_PATH} fill={`url(#${id}-shade)`} />
      {/* Star and sword: cut out, pressed bevel, then the blade's ridge and gem back in metal */}
      {holes({ fill: BACKDROP })}
      <g fill="none" stroke={INK} strokeLinejoin="round" opacity={0.3}>{holes({ strokeWidth: 2.6 })}</g>
      <g fill="none" stroke="#E6FAFF" strokeLinejoin="round" opacity={0.3} transform="translate(0.3 0.4)">{holes({ strokeWidth: 0.5 })}</g>
      <g fill="none" stroke={INK} strokeLinejoin="round">{holes({ strokeWidth: 0.9 })}</g>
      <g fill={`url(#${id}-tone)`}>{details}</g>
      {/* Rock: a metal gap where the blade enters, then the stone itself covering the buried blade */}
      {stone({ fill: 'none', stroke: `url(#${id}-tone)`, strokeWidth: 2.4 })}
      {stone({ fill: BACKDROP })}
      <g fill="none" stroke={INK} strokeLinejoin="round" opacity={0.3}>{stone({ strokeWidth: 2.6 })}</g>
      <g fill="none" stroke="#E6FAFF" strokeLinejoin="round" opacity={0.3} transform="translate(0.3 0.4)">{stone({ strokeWidth: 0.5 })}</g>
      <g fill="none" stroke={INK} strokeLinejoin="round">{stone({ strokeWidth: 0.9 })}</g>
      <path d={SHIELD_PATH} transform={SEAM_TRANSFORM} fill="none" stroke={INK} strokeWidth={1.5} />
      {/* Cracks and the broken tip only while it's broken — at rest the whole body underneath shows through */}
      {main && (
        <g clipPath={`url(#${id}-outer)`} style={{ opacity: settled ? 0 : 1, transition: settled ? 'opacity 0.6s ease-out' : 'none' }}>
          {crackLines({ fill: BACKDROP }, true)}
          {breakEdge({ fill: BACKDROP })}
          <g fill="none" stroke={INK} strokeLinejoin="round" style={crackStrokeStyle}>
            {crackLines({ strokeWidth: 0.9 }, true)}
          </g>
        </g>
      )}
    </>
  );

  const layers = (sfx: string, main: boolean) => IS_WEBKIT ? paintedLayers(main) : (
    <>
      <path d={SHIELD_PATH} transform="translate(0 1.4)" fill={INK} stroke={INK} strokeWidth={2.2} mask={`url(#${id}-edge${sfx})`} />
      <path d={SHIELD_PATH} fill="none" stroke={INK} strokeWidth={2.2} strokeLinejoin="round" mask={`url(#${id}-cracks${sfx})`} />
      <MeshFill x={0} y={0} width={64} height={64} mask={`url(#${id}-fill${sfx})`} colors={colors} speed={speed} />
      <g mask={`url(#${id}-fill${sfx})`} fill="none" strokeLinejoin="round" filter={`url(#${id}-soft)`}>
        <g stroke={INK} opacity={0.45}>
          <g mask={`url(#${id}-norock)`}>{holes({ strokeWidth: 2.6 })}</g>
          {stone({ strokeWidth: 2.6 })}
          {main && <g style={crackStrokeStyle}>{crackLines({ fill: 'none', strokeWidth: 2.6 }, true)}{breakEdge({ fill: 'none', strokeWidth: 2.6 })}</g>}
        </g>
        <g stroke="#E6FAFF" opacity={0.3} transform="translate(0.3 0.4)">
          <g mask={`url(#${id}-norock)`}>{holes({ strokeWidth: 0.5 })}</g>
          {stone({ strokeWidth: 0.5 })}
          {main && <g style={crackStrokeStyle}>{crackLines({ fill: 'none', strokeWidth: 0.5 }, true)}{breakEdge({ fill: 'none', strokeWidth: 0.5 })}</g>}
        </g>
      </g>
      <g mask={`url(#${id}-fill${sfx})`} fill="none" stroke={INK} strokeLinejoin="round">
        <g mask={`url(#${id}-norock)`}>{holes({ strokeWidth: 0.9 })}</g>
        {stone({ strokeWidth: 0.9 })}
        {main && <g style={crackStrokeStyle}>{crackLines({ fill: 'none', strokeWidth: 0.9 }, true)}{breakEdge({ fill: 'none', strokeWidth: 0.9 })}</g>}
        <path d={SHIELD_PATH} transform={SEAM_TRANSFORM} strokeWidth={1.5} />
      </g>
    </>
  );

  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ overflow: 'visible' }}>
      <defs>
        {IS_WEBKIT && (
          <>
            <linearGradient id={`${id}-tone`} x1="0" y1="0" x2="1" y2="1">
              {colors.map((c, i) => <stop key={i} offset={`${Math.round((i / (colors.length - 1)) * 100)}%`} stopColor={c} />)}
            </linearGradient>
            <radialGradient id={`${id}-sheen`} cx="0.32" cy="0.22" r="0.6">
              <stop offset="0" stopColor={colors[2]} stopOpacity={0.95} />
              <stop offset="1" stopColor={colors[2]} stopOpacity={0} />
            </radialGradient>
            <radialGradient id={`${id}-shade`} cx="0.75" cy="0.85" r="0.65">
              <stop offset="0" stopColor={colors[4]} stopOpacity={0.4} />
              <stop offset="1" stopColor={colors[4]} stopOpacity={0} />
            </radialGradient>
            <clipPath id={`${id}-inner`}>
              <path d={SHIELD_PATH} />
            </clipPath>
            <clipPath id={`${id}-outer`}>
              <path d={SHIELD_PATH} transform="translate(32 30) scale(1.08) translate(-32 -30)" />
            </clipPath>
          </>
        )}
        <clipPath id={`${id}-inside`}>
          <path d={SHIELD_PATH} />
        </clipPath>
        <filter id={`${id}-soft`} x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation="0.4" />
        </filter>
        <clipPath id={`${id}-rock`}>
          <path d={SHIELD_PATH} transform={ROCK_INSET} />
        </clipPath>
        {maskDefs('', true)}
        {maskDefs('-whole', false)}
        {/* Keeps the sword's ink from ticking across the gap band into the rock */}
        <mask id={`${id}-norock`} maskUnits="userSpaceOnUse" x="-10" y="-10" width="84" height="84">
          <rect x="-10" y="-10" width="84" height="84" fill="white" />
          {stone({ fill: 'black', stroke: 'black', strokeWidth: 2.4 })}
        </mask>
        {HALVES.map((h, i) => (
          <clipPath key={i} id={`${id}-half-${i}`}>
            <path d={h.d} />
          </clipPath>
        ))}
        {FRAGMENTS.map((f, i) => (
          <clipPath key={i} id={`${id}-frag-${i}`}>
            <path d={f.d} transform={MIRROR} />
          </clipPath>
        ))}
      </defs>

      {/* Intact body underneath once the pieces have settled — hides the hairline
          anti-alias seams where the clipped pieces meet */}
      <g transform={MIRROR} style={{ opacity: settled ? 1 : 0, transition: settled ? 'opacity 0.6s ease-out' : 'none' }}>
        {layers('-whole', false)}
      </g>
      {HALVES.map((h, i) => (
        <g key={i} style={pieceStyle(h)}>
          <g clipPath={`url(#${id}-half-${i})`}>
            <g transform={MIRROR}>{layers('', true)}</g>
          </g>
        </g>
      ))}
      {/* Lower section as three real pieces: they sit flush at rest, fall on the hit,
          and pull back together during the heal */}
      {FRAGMENTS.map((f, i) => (
        <g key={i} style={pieceStyle(f)}>
          <g transform={MIRROR} clipPath={`url(#${id}-frag-${i})`}>
            {layers('-whole', false)}
            <g mask={IS_WEBKIT ? undefined : `url(#${id}-fill-whole)`} clipPath={IS_WEBKIT ? `url(#${id}-inner)` : undefined} fill="none" strokeLinejoin="round" style={crackStrokeStyle}>
              <path d={f.d} transform={MIRROR} stroke={INK} strokeWidth={2.6} opacity={0.45} filter={IS_WEBKIT ? undefined : `url(#${id}-soft)`} />
              <path d={f.d} transform={MIRROR} stroke="#E6FAFF" strokeWidth={0.5} opacity={0.3} filter={IS_WEBKIT ? undefined : `url(#${id}-soft)`} />
              <path d={f.d} transform={MIRROR} stroke={INK} strokeWidth={0.9} />
            </g>
          </g>
        </g>
      ))}
    </svg>
  );
}
